import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import { cn } from "./_utils";

const nativeSelectVariants = cva(
  "appearance-none w-full bg-bg-input text-text-primary rounded-[var(--radius-control)] border transition-colors " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus " +
    "disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      state: {
        default: "border-border",
        error: "border-error",
      },
      selectSize: {
        sm: "h-7 pl-sm pr-xl text-sm",
        md: "h-9 pl-md pr-xl text-base",
      },
    },
    defaultVariants: { state: "default", selectSize: "md" },
  }
);

type NativeSelectProps = Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "size"
> &
  VariantProps<typeof nativeSelectVariants> & {
    ref?: React.Ref<HTMLSelectElement>;
    /** Class for the positioning wrapper (widths belong here). */
    wrapperClassName?: string;
  };

/**
 * Native `<select>` styled to match Input, with a chevron affordance. Use it
 * where the platform picker is the right interaction (dense admin forms,
 * table cells); use Select (Radix) when the popup needs rich item content.
 * `selectSize` mirrors Input's `inputSize` scale (native `size` is omitted to
 * avoid the prop-name collision).
 *
 * @example
 * <NativeSelect defaultValue="ops">
 *   <option value="ops">Ops Desk</option>
 *   <option value="billing">Billing Desk</option>
 * </NativeSelect>
 */
export function NativeSelect({
  className,
  state,
  selectSize,
  wrapperClassName,
  ref,
  children,
  ...props
}: NativeSelectProps) {
  return (
    <span className={cn("relative inline-block align-middle", wrapperClassName)}>
      <select
        ref={ref}
        className={cn(nativeSelectVariants({ state, selectSize }), className)}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute right-sm top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary opacity-60"
      />
    </span>
  );
}
